
<template lang="pug">
q-page.fit
  .absolute-full.column

    //- ツールバー
    .col-auto
      .flex.justify-between.q-ma-xs
        .flex.items-center

          //- 個人の追加
          q-btn.q-mx-sm(
            flat
            @click="detailShow()"
          ) 追加

    //- グリッド
    .col.grid
      .grid__table(
        v-show="showGrid"
        ref="table"
        :style="gridTableStyle"
        @mousedown.left.stop="onMouseDownLeft"
      )
        .grid__thead

          //- コーナー
          .grid__corner-area(@contextmenu.prevent)
            .grid__tr
              .grid__corner(:style="gridCornerStyle")

          //- 列ヘッダ
          .grid__column-header-area(@contextmenu.prevent)
            .grid__column-header-cursor(:style="gridColumnHeaderCursorStyle")
            grid-columns(
              :grid="grid"
              :cols="cols"
            )

        .grid__row-area

          //- 行ヘッダ
          .grid__row-header-area(
            @contextmenu.prevent
            @dblclick="onRowHeaderDoubleClick"
          )
            .grid__row-header-cursor(:style="gridRowHeaderCursorStyle")
            .grid__tr(v-for="row of rows")
              .grid__row-header.grid__left(:style="gridRowHeaderStyle") {{row.text}}

          //- セル
          .grid__cell-area(
            @dblclick="onCellDoubleClick"
            @mousedown.left.stop="onCellMouseDownLeft"
          )
            .grid__cell-cursor(:style="gridCellCursorStyle")
            grid-cells(
              :grid="grid"
              :rows="rows"
            )

            ContextMenu2(@beforeShow="onCellContext")

  //- 出退勤時間の入力
  PopupTimeRange(
    ref="popupTimeRange"
    :min="store.display_begin_time"
    :max="store.display_end_time"
    @confirm="timeRangeConfirm"
  )
    q-btn.q-mx-sm(
      color="secondary"
      flat
      @click="deleteTime"
    ) 休み

  //- 個人の入力
  PeopleDetail(
    ref="detail"
    @confirm="detailConfirm"
    @delete="detailDelete"
  )

</template>

<script>
import {timeIsValid, weeks} from 'helpers/date'
import {mapAuto} from 'helpers/vuex-bind'
import Person from 'models/Person'

import PopupTimeRange from 'components/PopupTimeRange'
import PeopleDetail from './PeopleDetail'
import AxiosMixin from 'mixins/axios'
import GridMixin from 'mixins/GridMixin'

export default {
  components: {PopupTimeRange, PeopleDetail},
  mixins: [AxiosMixin, GridMixin],

  props: {
    storeCode: String,
    floorCode: String,
  },

  data() {
    return {
      grid: {
        cellW:    70,
        cellH:    25,
        headerW:  60,
        headerH:  25,
        paddingW: 20,
        paddingH: 20,
      },

      people: {},

      clipboard: {},
    }
  },

  computed: {
    ...mapAuto(['stores']),

    viewKey() {
      return Object.values(this.$props).some(v => v == null)
        ? null
        : this.storeCode + '/' + this.floorCode
    },

    store() {
      return this.stores.find(store =>
        store.store_code == this.storeCode &&
        store.floor_code == this.floorCode) || {}
    },

    cols() {
      return [
        {
          text: 'グループ',
          type: 'Group',
        },
        ...weeks.slice(0, 7).map((v, week) => ({
          text: v.long,
          style: {color: v.color},
          type: 'Time',
          week,
        }))
      ]
    },

    rows() {
      const rows = []
      for(const person of Person.sort(this.people)) {
        rows.push({
          id: person.id,
          text: person.label,
          cells: [
            {text: person.group},
            ...weeks.slice(0, 7).map((v, x) => {
              return {
                text: person.getTimeLabel(x) || '-',
              }
            }),
          ],
        })
      }
      return rows
    },

  },

  watch: {
    viewKey() {
      this.refresh()
    },
  },

  mounted() {
    this.refresh()
  },

  methods: {

    async refresh() {
      try {
        if(this.viewKey != null) {

          // 表示を一旦クリアする
          this.people = []
          this.grid.cursor = {x: 0, y: 0}

          //
          const {data} = await this.axios.get('/api/people/' + this.viewKey)
          this.people = data.reduce((a, v) => ({...a, [v.id]: new Person(v)}), {})
        }
      }
      catch(e) {this.$handler(e)}
    },

    async createPerson(person) {
      const {data} = await this.axios.post('/api/people', {
        ...person,
        store_code: this.storeCode,
        floor_code: this.floorCode,
      })
      this.$set(this.people, data.id, new Person(data))
    },

    async updatePerson(person) {
      const orig = this.people[person.id]
      const diff = Object.entries(person).reduce((a, [k, v]) => ({...a, ...v !== orig[k] ? {[k]: v} : {}}), {})
      if(Object.keys(diff).length !== 0) {
        const {data} = await this.axios.put('/api/people/' + person.id, diff)
        this.$set(this.people, person.id, new Person(data))
      }
    },

    async deletePerson(data) {
      await this.axios.delete('/api/people/' + data.id)
      this.$delete(this.people, data.id)
    },

    getPerson(y) {
      return this.people[this.rows[y].id]
    },

    //-------------------------------------------------------------------------

    // 左クリックでセルの選択、左ドラッグでスクロール

    onMouseDownLeft(e) {
      this.beginDragScroll(e)
    },

    onCellMouseDownLeft(e) {
      this.selectCell(e)
      this.beginDragScroll(e)
    },

    // キー押下

    onKeyDown(e) {
      if(this.processKeyDown(e)) {
        return
      }

      // 削除 (Backspace/Deleteキー押下)
      if(e.key === 'Backspace' || e.key === 'Delete') {
        if(!this.isEditing) {
          this.callTyped('delete')
          e.preventDefault()
        }
        return
      }
    },

    // 編集開始 (cell領域をダブルクリック, Enterキー押下)

    onCellDoubleClick(e) {
      this.selectCell(e)
      this.callTyped('beginEdit')
    },

    onPressEnterBeginEdit() {
      this.callTyped('beginEdit')
    },

    // コピー/ペースト

    onClipboardCopy() {
      this.callTyped('clipboardCopy')
    },

    onClipboardPaste() {
      this.callTyped('clipboardPaste')
    },

    // cell領域のコンテキストメニュー
    onCellContext(e, options) {
      this.selectCell(e)
      options([{
        label: '編集', onClick: () => {
          this.callTyped('beginEdit')
        },
      }])
    },

    //-------------------------------------------------------------------------
    // 個人マスターの編集 (rowヘッダをダブルクリック)

    onRowHeaderDoubleClick(e) {
      const {y} = this.getCellPosition(e)
      const person = this.getPerson(y)
      this.detailShow(person)
    },

    detailShow(person = {}) {
      this.$refs.detail.show(person)
    },

    async detailConfirm(data) {
      try {
        if(data.id) {
          await this.updatePerson(data)
        }
        else {
          await this.createPerson(data)
        }
        this.$refs.detail.hide()
      }
      catch(e) {this.$refs.detail.handler(e)}
    },

    async detailDelete(data) {
      try {
        await this.deletePerson(data)
        this.$refs.detail.hide()
      }
      catch(e) {this.$refs.detail.handler(e)}
    },

    //-------------------------------------------------------------------------

    // 出勤退勤時刻の編集開始

    beginEditTime() {
      const {x, y} = this.grid.cursor
      const week = this.cols[x].week
      const person = this.getPerson(y)
      const {id} = person

      // カーソルが指している時刻を取得する、それが休みならなるべく近くの時刻を取得する
      let begin_time = this.store.begin_time
      let end_time   = this.store.end_time
      for(const i of [
        ...Array.from(Array(week + 1).keys(), i => week - i),
        ...Array.from(Array(7 - week).keys(), i => week + 1 + i),
      ]) {
        const begin_time_ = person['begin_time_' + i]
        const end_time_   = person['end_time_'   + i]
        if(timeIsValid(begin_time_, end_time_)) {
          begin_time = begin_time_
          end_time   = end_time_
          break
        }
      }

      //
      this.$refs.popupTimeRange.show(begin_time, end_time,
        {title: person.name + ' − ' + weeks[week].long, id, week})
    },

    // 出勤退勤時刻の削除

    deleteTime() {
      const {x, y} = this.grid.cursor
      const week = this.cols[x].week
      const person = this.getPerson(y)
      const {id} = person

      this.timeRangeConfirm('', '', {id, week})
    },

    // 出勤退勤時刻の編集終了

    async timeRangeConfirm(begin_time, end_time, {id, week}) {
      try {
        await this.updatePerson({
          id,
          ['begin_time_' + week]: begin_time,
          ['end_time_'   + week]: end_time,
        })
        this.$refs.popupTimeRange.hide()
      }
      catch(e) {this.$handler(e)}
    },

    // 出勤退勤時刻のコピー/ペースト

    clipboardCopyTime() {
      const {x, y} = this.grid.cursor
      const week = this.cols[x].week
      const person = this.getPerson(y)

      const begin_time = person['begin_time_' + week]
      const end_time   = person['end_time_'   + week]

      this.clipboard = {begin_time, end_time}
    },

    async clipboardPasteTime() {
      try {
        if('begin_time' in this.clipboard &&
           'end_time'   in this.clipboard) {

          const {x, y} = this.grid.cursor
          const week = this.cols[x].week
          const person = this.getPerson(y)

          await this.updatePerson({
            id:                     person.id,
            ['begin_time_' + week]: this.clipboard.begin_time,
            ['end_time_'   + week]: this.clipboard.end_time,
          })
        }
      }
      catch(e) {this.$handler(e)}
    },

    //-------------------------------------------------------------------------

    // グループの編集開始

    beginEditGroup() {
      const {y} = this.grid.cursor
      const person = this.getPerson(y)
      this.detailShow(person)
    },

    // グループのコピー/ペースト

    clipboardCopyGroup() {
      const {y} = this.grid.cursor
      const person = this.getPerson(y)

      this.clipboard = {group: person.group}
    },

    async clipboardPasteGroup() {
      try {
        if('group' in this.clipboard) {

          const {y} = this.grid.cursor
          const person = this.getPerson(y)

          await this.updatePerson({
            id:    person.id,
            group: this.clipboard.group,
          })
        }
      }
      catch(e) {this.$handler(e)}
    },

    //-------------------------------------------------------------------------

    callTyped(method, ...args) {
      const {x} = this.grid.cursor
      method += this.cols[x].type
      if(method in this) {
        this[method](...args)
      }
    },

  },
}
</script>
